import { useMemo, useState } from "react";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { Modal, ModalFooter } from "@/components/ui/modal";
import {
	useLockPeriod,
	usePeriods,
	useTrialBalance,
	useUnlockPeriod,
} from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import type { TagClass } from "@/lib/status";
import { apiError, Field, inputStyle } from "./_ui";

const MONTHS = [
	"janv.",
	"févr.",
	"mars",
	"avr.",
	"mai",
	"juin",
	"juil.",
	"août",
	"sept.",
	"oct.",
	"nov.",
	"déc.",
];
const PERIOD_CLASS: Record<string, TagClass> = {
	OPEN: "success",
	LOCKED: "danger",
	CLOSED: "neutral",
};

/** FIN-09 — États financiers (balance) + clôture des périodes. */
export function AdminStatementsPage() {
	const { data: periods } = usePeriods(2026);
	const monthly = useMemo(
		() => (periods ?? []).filter((p) => p.periodType === "MONTH"),
		[periods],
	);
	const [periodId, setPeriodId] = useState<string>("");
	const { data: tb } = useTrialBalance(periodId || undefined);
	const lock = useLockPeriod();
	const unlock = useUnlockPeriod();
	const [msg, setMsg] = useState<{
		kind: "danger" | "info";
		text: string;
	} | null>(null);

	const label = (p: { startsOn: string; fiscalYear: number }) =>
		`${MONTHS[Number(p.startsOn.slice(5, 7)) - 1]} ${p.fiscalYear}`;

	const doLock = (id: string) => {
		setMsg(null);
		lock.mutate(id, {
			onSuccess: () => setMsg({ kind: "info", text: "Période verrouillée." }),
			onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
		});
	};
	const [unlockId, setUnlockId] = useState<string | null>(null);
	const [unlockReason, setUnlockReason] = useState("");
	const unlockValid = unlockReason.trim().length >= 5;

	const openUnlock = (id: string) => {
		setUnlockReason("");
		setUnlockId(id);
	};
	const submitUnlock = () => {
		if (!unlockId || !unlockValid) return;
		unlock.mutate(
			{ id: unlockId, reason: unlockReason.trim() },
			{
				onSuccess: () => {
					setMsg({
						kind: "info",
						text: "Période déverrouillée (tracé dans l'audit).",
					});
					setUnlockId(null);
				},
				onError: (e) => setMsg({ kind: "danger", text: apiError(e) }),
			},
		);
	};

	return (
		<>
			<PageHead
				title="États financiers & clôture"
				sub="Balance générale SYSCOHADA · invariant ∑Débit = ∑Crédit"
			/>
			{msg && (
				<AlertBanner
					variant={msg.kind === "danger" ? "danger" : "info"}
					title={msg.text}
				/>
			)}

			<Card>
				<CardHead
					title="Balance générale"
					right={
						<select
							style={{ ...inputStyle, width: 170 }}
							value={periodId}
							onChange={(e) => setPeriodId(e.target.value)}
						>
							<option value="">Toutes périodes</option>
							{monthly.map((p) => (
								<option key={p.id} value={p.id}>
									{label(p)}
								</option>
							))}
						</select>
					}
				/>
				<CardBody>
					{tb && (
						<AlertBanner
							variant={tb.balanced ? "info" : "danger"}
							title={
								tb.balanced
									? `Balance équilibrée — ∑Débit = ∑Crédit = ${fmtFCFA(tb.totalDebit)}`
									: `Déséquilibre détecté : Δ = ${fmtFCFA(tb.totalDebit - tb.totalCredit)}`
							}
						/>
					)}
					<div
						className="table-wrap"
						style={{ margin: 0, maxHeight: 480, overflow: "auto" }}
					>
						<table className="table">
							<thead>
								<tr>
									<th>Compte</th>
									<th>Libellé</th>
									<th className="right">Débit</th>
									<th className="right">Crédit</th>
									<th className="right">Solde</th>
								</tr>
							</thead>
							<tbody>
								{(tb?.rows ?? []).length === 0 && (
									<tr>
										<td
											colSpan={5}
											className="muted"
											style={{ textAlign: "center", padding: 24 }}
										>
											Aucun mouvement.
										</td>
									</tr>
								)}
								{(tb?.rows ?? []).map((r) => (
									<tr key={r.accountCode}>
										<td className="mono">
											<strong>{r.accountCode}</strong>
										</td>
										<td>{r.label}</td>
										<td className="right tn">{fmtFCFA(r.totalDebit)}</td>
										<td className="right tn">{fmtFCFA(r.totalCredit)}</td>
										<td className="right tn">
											<strong>{fmtFCFA(r.balance)}</strong>
										</td>
									</tr>
								))}
							</tbody>
							{(tb?.rows ?? []).length > 0 && (
								<tfoot>
									<tr style={{ fontWeight: 700 }}>
										<td colSpan={2}>TOTAL</td>
										<td className="right tn">{fmtFCFA(tb!.totalDebit)}</td>
										<td className="right tn">{fmtFCFA(tb!.totalCredit)}</td>
										<td className="right tn">
											{fmtFCFA(tb!.totalDebit - tb!.totalCredit)}
										</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
				</CardBody>
			</Card>

			<Card style={{ marginTop: 16 }}>
				<CardHead
					title="Clôture mensuelle"
					sub="Verrouillage = aucune écriture rétroactive possible"
				/>
				<CardBody>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Période</th>
									<th>Du → au</th>
									<th>Statut</th>
									<th />
								</tr>
							</thead>
							<tbody>
								{monthly.map((p) => (
									<tr key={p.id}>
										<td>
											<strong>{label(p)}</strong>
										</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{p.startsOn} → {p.endsOn}
										</td>
										<td>
											<Tag variant={PERIOD_CLASS[p.status] ?? "neutral"}>
												{p.status}
											</Tag>
										</td>
										<td>
											{p.status === "OPEN" ? (
												<AdminButton
													variant="ghost"
													size="sm"
													onClick={() => doLock(p.id)}
												>
													<AdminIcon n="lock" s={13} /> Verrouiller
												</AdminButton>
											) : p.status === "LOCKED" ? (
												<AdminButton
													variant="ghost"
													size="sm"
													onClick={() => openUnlock(p.id)}
												>
													<AdminIcon n="key" s={13} /> Déverrouiller
												</AdminButton>
											) : null}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardBody>
			</Card>

			{unlockId && (
				<Modal
					title="Déverrouiller la période"
					subtitle="Action sensible (Super Admin) — elle autorise à nouveau des écritures rétroactives et est tracée dans l'audit."
					onClose={() => setUnlockId(null)}
				>
					<form
						onSubmit={(ev) => {
							ev.preventDefault();
							submitUnlock();
						}}
					>
						<Field label="Motif du déverrouillage (≥ 5 caractères)">
							<textarea
								autoFocus
								style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
								value={unlockReason}
								onChange={(ev) => setUnlockReason(ev.target.value)}
								maxLength={300}
								placeholder="Ex. régularisation d'une facture fournisseur omise…"
							/>
						</Field>
						{unlockReason.length > 0 && !unlockValid && (
							<div
								style={{
									fontSize: 11.5,
									color: "var(--a-danger)",
									marginTop: 6,
								}}
							>
								Le motif doit faire au moins 5 caractères.
							</div>
						)}
						<ModalFooter>
							<button
								type="button"
								className="btn btn-ghost"
								onClick={() => setUnlockId(null)}
							>
								Annuler
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={!unlockValid || unlock.isPending}
								style={{
									opacity: !unlockValid || unlock.isPending ? 0.5 : 1,
									cursor: !unlockValid ? "not-allowed" : "pointer",
								}}
							>
								{unlock.isPending ? "Déverrouillage…" : "Déverrouiller"}
							</button>
						</ModalFooter>
					</form>
				</Modal>
			)}
		</>
	);
}
